import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import {
    ArrowLeft, ChevronLeft, ChevronRight, Search, Filter, X,
    User, Phone, Mail, MapPin, CheckCircle2, Upload, CreditCard, Package, Trash2, Truck, Pencil
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import EventPiecesJointes from '../components/EventPiecesJointes';
import SousTraitanceForm from '../components/SousTraitanceForm';
import STFactureSection from '../components/STFactureSection';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_COLORS = {
    'Preparation': { bg: 'bg-purple-400', text: 'text-white', dot: 'bg-purple-400' },
    'Preparation et chargement': { bg: 'bg-purple-600', text: 'text-white', dot: 'bg-purple-600' },
    'Chargement': { bg: 'bg-blue-600', text: 'text-white', dot: 'bg-blue-600' },
    'Dechargement': { bg: 'bg-cyan-500', text: 'text-white', dot: 'bg-cyan-500' },
    'Demenagement local': { bg: 'bg-sky-300', text: 'text-white', dot: 'bg-sky-300' },
    'Stockage': { bg: 'bg-rose-500', text: 'text-white', dot: 'bg-rose-500' },
    'Sortie garde meuble': { bg: 'bg-pink-400', text: 'text-white', dot: 'bg-pink-400' },
    'Route sur 1 journee': { bg: 'bg-gray-500', text: 'text-white', dot: 'bg-gray-500' },
    'Livraison': { bg: 'bg-amber-400', text: 'text-white', dot: 'bg-amber-400' },
    'Sous-traitance': { bg: 'bg-orange-700', text: 'text-white', dot: 'bg-orange-700' },
};

// Map type_prestation enum values (sous-traitance) -> display label
const ST_TYPE_LABELS = {
    'demenagement_local': 'Démén. local',
    'demenagement_longue_distance': 'Démén. longue distance',
    'chargement': 'Chargement',
    'dechargement': 'Déchargement',
    'garde_meuble': 'Garde-meuble',
    'autre': 'Autre',
};

const JOURS_SEMAINE = ['Dim.', 'Lun.', 'Mar.', 'Mer.', 'Jeu.', 'Ven.', 'Sam.'];
const MOIS_NOMS = ['Janvier', 'Fevrier', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Aout', 'Septembre', 'Octobre', 'Novembre', 'Decembre'];

const ALL_TYPES = [
    'Preparation', 'Preparation et chargement', 'Chargement', 'Dechargement',
    'Demenagement local', 'Stockage', 'Sortie garde meuble', 'Route sur 1 journee', 'Livraison',
];

const PAIEMENT_OPTIONS = [
    { value: 'virement_recu', label: 'Virement recu', needsFile: false },
    { value: 'virement_attente', label: 'Virement en attente', needsFile: false },
    { value: 'cheque_recupere', label: 'Cheque recupere', needsFile: true },
    { value: 'caution_recuperee', label: 'Caution recuperee', needsFile: true },
];

export default function AgendaDemenagement() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const isAdmin = user?.role === 'admin';
    const today = new Date();
    const [currentMonth, setCurrentMonth] = useState(today.getMonth() + 1);
    const [currentYear, setCurrentYear] = useState(today.getFullYear());
    const [events, setEvents] = useState([]);
    const [search, setSearch] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [loading, setLoading] = useState(true);
    // 'month' = grid mensuel ; 'list' = vue liste façon mobile (jours empilés)
    const [viewMode, setViewMode] = useState(() => {
        if (typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches) return 'list';
        return 'month';
    });

    // List view (60 jours roulants depuis aujourd'hui)
    const [listEvents, setListEvents] = useState([]);
    const [listLoading, setListLoading] = useState(false);

    // Event detail modal
    const [detailOpen, setDetailOpen] = useState(false);
    const [detailData, setDetailData] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);

    // Day-list modal (shows ALL events when a day has > 3 events)
    const [dayListOpen, setDayListOpen] = useState(false);
    const [dayListDate, setDayListDate] = useState('');
    const [dayListEvents, setDayListEvents] = useState([]);

    // Sous-traitance form
    const [stFormOpen, setStFormOpen] = useState(false);
    const [stEditId, setStEditId] = useState(null);

    const openSousTraitanceForm = (id = null) => {
        setStEditId(id);
        setStFormOpen(true);
    };

    // Livraison faite modal
    const [paiementOpen, setPaiementOpen] = useState(false);
    const [paiementType, setPaiementType] = useState('');
    const [justificatifFile, setJustificatifFile] = useState(null);
    const [paiementSaving, setPaiementSaving] = useState(false);

    // Delete event confirmation
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [deletingEvent, setDeletingEvent] = useState(false);

    const handleDeleteEvent = async () => {
        if (!detailData?.id) return;
        setDeletingEvent(true);
        try {
            await axios.delete(`${API_URL}/api/agenda-demenagement/event/${detailData.id}`, { withCredentials: true });
            toast.success('Evenement supprime');
            setDeleteConfirmOpen(false);
            setDetailOpen(false);
            fetchEvents();
            if (viewMode === 'list') fetchListEvents();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        } finally {
            setDeletingEvent(false);
        }
    };

    const fetchEvents = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ month: currentMonth, year: currentYear });
            if (search) params.set('search', search);
            if (typeFilter) params.set('type_filter', typeFilter);
            const res = await axios.get(`${API_URL}/api/agenda-demenagement?${params}`, { withCredentials: true });
            setEvents(res.data.events || []);
        } catch {
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchEvents(); }, [currentMonth, currentYear, typeFilter]);

    useEffect(() => {
        const t = setTimeout(() => fetchEvents(), 400);
        return () => clearTimeout(t);
    }, [search]);

    // Fetch rolling 60-day window for list view
    const fetchListEvents = async () => {
        setListLoading(true);
        try {
            const start = new Date();
            const end = new Date();
            end.setDate(start.getDate() + 60);
            const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
            const params = new URLSearchParams({ date_start: fmt(start), date_end: fmt(end) });
            if (search) params.set('search', search);
            if (typeFilter) params.set('type_filter', typeFilter);
            const res = await axios.get(`${API_URL}/api/agenda-demenagement?${params}`, { withCredentials: true });
            setListEvents(res.data.events || []);
        } catch {
            toast.error('Erreur lors du chargement');
        } finally {
            setListLoading(false);
        }
    };

    useEffect(() => {
        if (viewMode === 'list') fetchListEvents();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewMode, typeFilter]);

    useEffect(() => {
        if (viewMode !== 'list') return;
        const t = setTimeout(() => fetchListEvents(), 400);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

    const goToday = () => { setCurrentMonth(today.getMonth() + 1); setCurrentYear(today.getFullYear()); };
    const goPrev = () => { if (currentMonth === 1) { setCurrentMonth(12); setCurrentYear(currentYear - 1); } else setCurrentMonth(currentMonth - 1); };
    const goNext = () => { if (currentMonth === 12) { setCurrentMonth(1); setCurrentYear(currentYear + 1); } else setCurrentMonth(currentMonth + 1); };

    const openEventDetail = async (eventId) => {
        setDetailLoading(true);
        setDetailOpen(true);
        try {
            const res = await axios.get(`${API_URL}/api/agenda-demenagement/event/${eventId}`, { withCredentials: true });
            setDetailData(res.data);
        } catch {
            toast.error('Erreur');
            setDetailOpen(false);
        } finally {
            setDetailLoading(false);
        }
    };

    const handleLivraisonFaite = () => {
        setPaiementType('');
        setJustificatifFile(null);
        setPaiementOpen(true);
    };

    const submitPaiement = async () => {
        if (!paiementType) { toast.error('Selectionnez un type de paiement'); return; }
        const opt = PAIEMENT_OPTIONS.find(o => o.value === paiementType);
        if (opt?.needsFile && !justificatifFile) { toast.error('Justificatif requis'); return; }

        setPaiementSaving(true);
        try {
            let justificatifUrl = '';
            if (justificatifFile) {
                const reader = new FileReader();
                const base64 = await new Promise((resolve) => {
                    reader.onload = () => resolve(reader.result);
                    reader.readAsDataURL(justificatifFile);
                });
                const uploadRes = await axios.post(`${API_URL}/api/clients/${detailData.client_id}/livraison/upload`, {
                    file_data: base64, file_name: justificatifFile.name,
                }, { withCredentials: true });
                justificatifUrl = uploadRes.data.url;
            }

            await axios.post(`${API_URL}/api/clients/${detailData.client_id}/livraison/complete`, {
                paiement_type: paiementType, justificatif_url: justificatifUrl,
            }, { withCredentials: true });

            toast.success('Livraison terminee');
            setPaiementOpen(false);
            setDetailOpen(false);
            fetchEvents();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        } finally {
            setPaiementSaving(false);
        }
    };

    // Build calendar grid
    const calendarDays = useMemo(() => {
        const firstDay = new Date(currentYear, currentMonth - 1, 1);
        const lastDay = new Date(currentYear, currentMonth, 0);
        const startDow = firstDay.getDay();
        const daysInMonth = lastDay.getDate();
        const days = [];
        const prevMonthLast = new Date(currentYear, currentMonth - 1, 0).getDate();
        for (let i = startDow - 1; i >= 0; i--) {
            const d = prevMonthLast - i;
            const m = currentMonth === 1 ? 12 : currentMonth - 1;
            const y = currentMonth === 1 ? currentYear - 1 : currentYear;
            days.push({ day: d, month: m, year: y, isCurrentMonth: false });
        }
        for (let d = 1; d <= daysInMonth; d++) {
            days.push({ day: d, month: currentMonth, year: currentYear, isCurrentMonth: true });
        }
        const remaining = 42 - days.length;
        for (let d = 1; d <= remaining; d++) {
            const m = currentMonth === 12 ? 1 : currentMonth + 1;
            const y = currentMonth === 12 ? currentYear + 1 : currentYear;
            days.push({ day: d, month: m, year: y, isCurrentMonth: false });
        }
        return days;
    }, [currentMonth, currentYear]);

    const eventsByDate = useMemo(() => {
        const map = {};
        events.forEach(ev => { if (!ev.date) return; if (!map[ev.date]) map[ev.date] = []; map[ev.date].push(ev); });
        return map;
    }, [events]);

    // Build 60-day rolling list (only days with events) for the List view
    const listDays = useMemo(() => {
        const grouped = {};
        listEvents.forEach(ev => {
            if (!ev.date) return;
            if (!grouped[ev.date]) grouped[ev.date] = [];
            grouped[ev.date].push(ev);
        });
        const sortedKeys = Object.keys(grouped).sort();
        return sortedKeys.map(dateKey => {
            const [yy, mm, dd] = dateKey.split('-').map(Number);
            const d = new Date(yy, mm - 1, dd);
            return {
                dateKey,
                date: d,
                day: dd,
                month: mm,
                year: yy,
                dow: d.getDay(),
                events: grouped[dateKey].sort((a, b) => (a.heure || '').localeCompare(b.heure || '')),
            };
        });
    }, [listEvents]);

    const isToday = (d) => d.day === today.getDate() && d.month === today.getMonth() + 1 && d.year === today.getFullYear();
    const formatDateKey = (d) => `${d.year}-${String(d.month).padStart(2, '0')}-${String(d.day).padStart(2, '0')}`;

    const getDayResources = (dateKey) => {
        const dayEvents = eventsByDate[dateKey] || [];
        let totalMovers = 0, total20 = 0, total30 = 0;
        dayEvents.forEach(ev => {
            // Skip cancelled events from the daily totals — they no longer
            // consume effectifs / camions.
            if (ev.cancelled) return;
            totalMovers += ev.demenageurs || 0;
            total20 += ev.camion_20m3 || 0;
            total30 += ev.camion_30m3 || 0;
        });
        return { totalMovers, total20, total30, events: dayEvents };
    };

    return (
        <Layout>
            <div className="space-y-5" data-testid="agenda-demenagement-page">
                <div className="flex items-center gap-3">
                    <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-gray-500 hover:text-[#1B365D] transition-colors text-sm" data-testid="agenda-back-btn">
                        <ArrowLeft className="w-4 h-4" /> Retour
                    </button>
                    <h1 className="text-2xl font-bold text-[#1B365D]" style={{ fontFamily: 'Outfit, sans-serif' }}>Calendrier des prestations</h1>
                </div>

                <div className="flex items-center gap-4">
                    <div className="relative flex-1 max-w-sm">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <Input placeholder="Rechercher un client..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 text-sm" data-testid="agenda-search" />
                    </div>
                    <div className="relative min-w-[200px]">
                        <Filter className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
                            className="w-full border border-gray-200 rounded-lg pl-9 pr-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-200 appearance-none"
                            data-testid="agenda-type-filter">
                            <option value="">Tous les types</option>
                            {ALL_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                        <ChevronRight className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none rotate-90" />
                    </div>
                </div>

                <div className="flex items-center justify-between gap-3 bg-white border border-gray-200 rounded-xl px-3 py-2 shadow-sm">
                    <div className="flex items-center gap-2">
                        <button onClick={goPrev} className="p-2 rounded-lg hover:bg-gray-100 transition-colors" data-testid="agenda-prev-btn" aria-label="Précédent"><ChevronLeft className="w-4 h-4 text-gray-600" /></button>
                        <button onClick={goNext} className="p-2 rounded-lg hover:bg-gray-100 transition-colors" data-testid="agenda-next-btn" aria-label="Suivant"><ChevronRight className="w-4 h-4 text-gray-600" /></button>
                        <Button variant="outline" className="text-xs h-8 px-3 border-gray-200" onClick={goToday} data-testid="agenda-today-btn">Aujourd'hui</Button>
                    </div>
                    <span className="text-base sm:text-lg font-bold text-[#1B365D] tracking-tight" data-testid="agenda-month-label">
                        {MOIS_NOMS[currentMonth - 1]} <span className="text-[#C9A962]">{currentYear}</span>
                    </span>
                    <div className="flex items-center gap-2">
                        <div className="inline-flex bg-gray-100 rounded-lg p-0.5" data-testid="agenda-view-toggle">
                            <button
                                onClick={() => setViewMode('month')}
                                className={`px-2.5 py-1 rounded-md text-[11px] font-semibold transition-colors ${viewMode === 'month' ? 'bg-white text-[#1B365D] shadow-sm' : 'text-gray-500'}`}
                                data-testid="agenda-view-month"
                            >
                                Mois
                            </button>
                            <button
                                onClick={() => setViewMode('list')}
                                className={`px-2.5 py-1 rounded-md text-[11px] font-semibold transition-colors ${viewMode === 'list' ? 'bg-white text-[#1B365D] shadow-sm' : 'text-gray-500'}`}
                                data-testid="agenda-view-list"
                            >
                                Liste
                            </button>
                        </div>
                        {isAdmin && (
                            <button
                                onClick={() => openSousTraitanceForm()}
                                className="inline-flex items-center gap-1.5 bg-orange-700 hover:bg-orange-800 text-white text-xs font-semibold px-3 py-1.5 rounded-lg shadow-sm transition-colors"
                                data-testid="agenda-st-add-btn"
                                title="Ajouter un déménagement en sous-traitance"
                            >
                                <Truck className="w-3.5 h-3.5" />
                                <span>+ Sous-traitance</span>
                            </button>
                        )}
                    </div>
                </div>

                {viewMode === 'month' && (
                    <>
                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden" data-testid="agenda-calendar-grid">
                    <div className="grid grid-cols-7 border-b border-gray-200 bg-gradient-to-b from-[#1B365D]/5 to-transparent">
                        {JOURS_SEMAINE.map(j => (
                            <div key={j} className="px-2 py-3 text-center text-[11px] font-bold text-[#1B365D]/70 uppercase tracking-widest border-r border-gray-100 last:border-r-0">{j}</div>
                        ))}
                    </div>
                    <div className="grid grid-cols-7">
                        {calendarDays.map((d, i) => {
                            const dateKey = formatDateKey(d);
                            const res = getDayResources(dateKey);
                            const isTd = isToday(d);
                            const hasActivity = (res.totalMovers + res.total20 + res.total30) > 0;
                            return (
                                <div key={i} className={`min-h-[120px] border-r border-b border-gray-100 last:border-r-0 p-1.5 transition-colors hover:bg-gray-50/60 ${d.isCurrentMonth ? 'bg-white' : 'bg-gray-50/40'} ${isTd ? 'ring-2 ring-inset ring-[#C9A962]/50' : ''}`} data-testid={`agenda-cell-${dateKey}`}>
                                    <div className="flex items-center justify-between mb-1 px-0.5">
                                        {isTd ? (
                                            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#1B365D] text-white text-[11px] font-bold shadow-sm">{d.day}</span>
                                        ) : (
                                            <span className={`text-[13px] font-semibold ${d.isCurrentMonth ? 'text-gray-700' : 'text-gray-300'}`}>{d.day}</span>
                                        )}
                                    </div>
                                    {d.isCurrentMonth && (
                                        <div
                                            className={`rounded-md text-[10px] px-1.5 py-0.5 mb-1 truncate font-semibold tracking-wide border ${
                                                hasActivity
                                                    ? 'bg-emerald-500 text-white border-emerald-600 shadow-sm'
                                                    : 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                            }`}
                                            title={`${res.totalMovers} effectif(s) / ${res.total20} camion(s) 20m³ / ${res.total30} camion(s) 30m³`}
                                        >
                                            {res.totalMovers} eff / {res.total20} x 20 m3 / {res.total30} x 30m3
                                        </div>
                                    )}
                                    {res.events.slice(0, 3).map((ev, idx) => {
                                        const isST = ev.event_type === 'sous_traitance';
                                        const tc = isST
                                            ? TYPE_COLORS['Sous-traitance']
                                            : (TYPE_COLORS[ev.type_prestation] || { bg: 'bg-gray-400', text: 'text-white' });
                                        const stTitle = isST ? `[ST] ${ev.client_nom}${ev.sous_traitant_nom ? ' • ' + ev.sous_traitant_nom : ''}` : `${ev.client_nom} - ${ev.type_prestation}`;
                                        return (
                                            <button key={idx} onClick={() => openEventDetail(ev.id)}
                                                className={`${ev.cancelled ? 'bg-red-200 text-red-900 line-through opacity-70' : `${tc.bg} ${tc.text}`} rounded-md text-[10px] px-1.5 py-0.5 mb-0.5 truncate w-full text-left cursor-pointer hover:opacity-90 hover:shadow-sm transition-all border border-white/10`}
                                                title={ev.cancelled ? `Annulee : ${ev.cancel_raison || ''}` : stTitle}
                                                data-testid={`event-badge-${ev.id}`}>
                                                {ev.heure ? <span className="font-semibold mr-1 opacity-90">{ev.heure}</span> : null}{isST ? <span className="font-bold mr-0.5">ST</span> : null} {ev.client_nom}
                                            </button>
                                        );
                                    })}
                                    {res.events.length > 3 && (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setDayListDate(dateKey);
                                                setDayListEvents(res.events);
                                                setDayListOpen(true);
                                            }}
                                            className="text-[10px] text-[#1B365D] font-semibold pl-1 hover:underline cursor-pointer w-full text-left"
                                            data-testid={`day-more-${dateKey}`}
                                        >
                                            +{res.events.length - 3} autre(s)
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="flex items-center gap-5 flex-wrap px-1" data-testid="agenda-legend">
                    <span className="text-xs font-bold text-blue-700 uppercase tracking-wide">Legende</span>
                    {ALL_TYPES.map(t => {
                        const tc = TYPE_COLORS[t] || { dot: 'bg-gray-400' };
                        return (
                            <div key={t} className="flex items-center gap-1.5">
                                <span className={`w-2.5 h-2.5 rounded-full ${tc.dot}`} />
                                <span className="text-xs text-gray-600">{t}</span>
                            </div>
                        );
                    })}
                </div>
                    </>
                )}

                {viewMode === 'list' && (
                    <ListView
                        days={listDays}
                        loading={listLoading}
                        onEventClick={openEventDetail}
                        today={today}
                    />
                )}
            </div>

            {/* Sous-traitance Form Modal */}
            <SousTraitanceForm
                open={stFormOpen}
                onOpenChange={setStFormOpen}
                eventId={stEditId}
                onSaved={() => { setStEditId(null); fetchEvents(); }}
            />

            {/* Day-list Modal — shows ALL events when a day has more than 3 events */}
            <Dialog open={dayListOpen} onOpenChange={setDayListOpen}>
                <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="day-list-modal">
                    <DialogTitle className="text-lg font-bold text-[#1B365D]">
                        Événements du {dayListDate ? new Date(dayListDate + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : ''}
                    </DialogTitle>
                    <div className="space-y-2 mt-2">
                        {dayListEvents.map((ev) => {
                            const isST = ev.event_type === 'sous_traitance';
                            const tc = isST
                                ? TYPE_COLORS['Sous-traitance']
                                : (TYPE_COLORS[ev.type_prestation] || { bg: 'bg-gray-400', text: 'text-white' });
                            return (
                                <button
                                    key={ev.id}
                                    onClick={() => {
                                        setDayListOpen(false);
                                        openEventDetail(ev.id);
                                    }}
                                    className={`${ev.cancelled ? 'bg-red-50 border-red-300 text-red-900 line-through' : `${tc.bg} ${tc.text} border-transparent`} w-full text-left rounded-lg px-3 py-2 text-sm font-medium border hover:opacity-90 hover:shadow-sm transition-all flex items-center gap-2`}
                                    data-testid={`day-list-event-${ev.id}`}
                                >
                                    {ev.heure && <span className="font-bold opacity-90">{ev.heure}</span>}
                                    {isST && <span className="bg-white/30 text-[10px] font-bold px-1.5 py-0.5 rounded">ST</span>}
                                    <span className="flex-1 truncate">{ev.client_nom}</span>
                                    <span className="text-[10px] uppercase opacity-80">{isST ? (ev.sous_traitant_nom || 'Sous-traitance') : ev.type_prestation}</span>
                                </button>
                            );
                        })}
                        {dayListEvents.length === 0 && (
                            <div className="text-center text-gray-400 py-6 italic">Aucun événement</div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>


            {/* Event Detail Modal */}
            <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto p-0" data-testid="event-detail-modal">
                    <DialogTitle className="sr-only">Detail evenement</DialogTitle>
                    {detailLoading ? (
                        <div className="flex items-center justify-center p-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1B365D]" /></div>
                    ) : detailData && (
                        <div>
                            {detailData.cancelled && (
                                <div className="bg-red-50 border-b-2 border-red-300 px-6 py-3" data-testid="event-cancelled-banner">
                                    <div className="flex items-start gap-2">
                                        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-red-100 text-red-700 shrink-0 font-bold text-sm">!</span>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-bold text-red-700 uppercase tracking-wide">Prestation annulée</p>
                                            {detailData.cancel_raison && (
                                                <p className="text-sm text-red-700 italic mt-0.5 whitespace-pre-wrap break-words">« {detailData.cancel_raison} »</p>
                                            )}
                                            {detailData.cancelled_by && (
                                                <p className="text-[11px] text-red-600/80 mt-1">
                                                    Annulée {detailData.cancelled_at ? `le ${new Date(detailData.cancelled_at).toLocaleDateString('fr-FR')}` : ''} par {detailData.cancelled_by}
                                                </p>
                                            )}
                                        </div>
                                        {detailData.client_id && (
                                            <button
                                                type="button"
                                                onClick={async () => {
                                                    try {
                                                        await axios.post(`${API_URL}/api/clients/${detailData.client_id}/uncancel`, {}, { withCredentials: true });
                                                        toast.success('Prestation reactivee');
                                                        // Reload event detail and refetch the calendar
                                                        if (typeof openEventDetail === 'function') openEventDetail(detailData.id);
                                                        if (typeof fetchEvents === 'function') fetchEvents();
                                                        else window.location.reload();
                                                    } catch (err) {
                                                        toast.error(err.response?.data?.detail || 'Erreur');
                                                    }
                                                }}
                                                className="shrink-0 inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-md px-3 py-1.5 transition-colors"
                                                data-testid="event-uncancel-btn"
                                            >
                                                Réactiver
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}
                            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                                <div>
                                    <h2 className={`text-lg font-bold ${detailData.cancelled ? 'text-red-700 line-through' : 'text-[#1B365D]'}`}>{detailData.type_prestation}</h2>
                                    <p className={`text-sm ${detailData.cancelled ? 'text-red-400 line-through' : 'text-gray-400'}`}>
                                        {detailData.date ? new Date(detailData.date + 'T00:00').toLocaleDateString('fr-FR') : ''} {detailData.heure || ''}
                                    </p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={async () => {
                                            // The clipboard write MUST start within the user gesture
                                            // tick (otherwise iOS/iPadOS Safari silently rejects it
                                            // because the "user activation" is consumed by `await`).
                                            //
                                            // Modern pattern: pass a Promise<Blob> to ClipboardItem.
                                            // Safari (and Chrome/Edge) keep the gesture alive while
                                            // the inner promise resolves, so the link generation can
                                            // be async without losing permission.
                                            const buildLinkBlob = async () => {
                                                const res = await axios.post(
                                                    `${API_URL}/api/agenda-demenagement/event/${detailData.id}/generate-demenageur-link`,
                                                    {},
                                                    { withCredentials: true }
                                                );
                                                const fullLink = `${window.location.origin}${res.data.link}`;
                                                return { fullLink, blob: new Blob([fullLink], { type: 'text/plain' }) };
                                            };

                                            toast.loading('Generation du lien...', { id: 'dem-link' });

                                            // 1) Modern path: navigator.clipboard.write with Promise<Blob>.
                                            //    Works on iOS Safari 13.4+, Chrome 76+, Edge.
                                            if (typeof window.ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
                                                try {
                                                    const itemPromise = buildLinkBlob().then(({ blob }) => blob);
                                                    const item = new window.ClipboardItem({ 'text/plain': itemPromise });
                                                    await navigator.clipboard.write([item]);
                                                    toast.success('Lien copié 🥳', { id: 'dem-link', duration: 3000 });
                                                    return;
                                                } catch {
                                                    // Fall through to legacy path
                                                }
                                            }

                                            // 2) Legacy path: generate link first, then try writeText / execCommand.
                                            try {
                                                const { fullLink } = await buildLinkBlob();
                                                let copied = false;
                                                try {
                                                    await navigator.clipboard.writeText(fullLink);
                                                    copied = true;
                                                } catch {
                                                    try {
                                                        const ta = document.createElement('textarea');
                                                        ta.value = fullLink;
                                                        ta.setAttribute('readonly', '');
                                                        ta.style.position = 'fixed';
                                                        ta.style.top = '0';
                                                        ta.style.left = '0';
                                                        ta.style.opacity = '0';
                                                        document.body.appendChild(ta);
                                                        ta.focus();
                                                        ta.select();
                                                        ta.setSelectionRange(0, ta.value.length);
                                                        copied = document.execCommand('copy');
                                                        document.body.removeChild(ta);
                                                    } catch { copied = false; }
                                                }
                                                if (copied) {
                                                    toast.success('Lien copié 🥳', { id: 'dem-link', duration: 3000 });
                                                } else {
                                                    // Last resort: show the link so the user can long-press to copy.
                                                    toast.error('Impossible de copier automatiquement. Lien : ' + fullLink, { id: 'dem-link', duration: 12000 });
                                                }
                                            } catch {
                                                toast.error('Erreur generation lien', { id: 'dem-link' });
                                            }
                                        }}
                                        className="px-3 py-1.5 text-xs font-bold rounded-md border border-emerald-600 text-emerald-700 hover:bg-emerald-600 hover:text-white transition-colors"
                                        data-testid="event-demenageur-link-btn"
                                        title="Copier le lien pour l'equipe demenageurs (WhatsApp/SMS)"
                                    >
                                        Lien demenageur
                                    </button>
                                    <button
                                        type="button"
                                        onClick={async () => {
                                            const reqUrl = `${API_URL}/api/agenda-demenagement/event/${detailData.id}/lettre-voiture-pdf`;
                                            toast.loading('Génération LV...', { id: 'lv-pdf' });
                                            try {
                                                const res = await fetch(reqUrl, { credentials: 'include' });
                                                if (!res.ok) {
                                                    let detail = `Erreur ${res.status}`;
                                                    try {
                                                        const j = await res.json();
                                                        detail = j.detail || j.message || detail;
                                                    } catch { /* keep status */ }
                                                    console.error('LV server error', res.status, detail);
                                                    toast.error(detail, { id: 'lv-pdf', duration: 10000 });
                                                    return;
                                                }
                                                const blob = await res.blob();
                                                const url = URL.createObjectURL(blob);
                                                const nom = (detailData.client?.nom || 'lettre_voiture').replace(/\s+/g, '_');
                                                const filename = `LV_${nom}_${detailData.date || ''}.pdf`;
                                                const a = document.createElement('a');
                                                a.href = url;
                                                a.download = filename;
                                                a.rel = 'noopener';
                                                document.body.appendChild(a);
                                                a.click();
                                                a.remove();
                                                const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
                                                if (isIOS) {
                                                    setTimeout(() => { try { window.open(url, '_blank'); } catch { /* no-op */ } }, 100);
                                                }
                                                setTimeout(() => URL.revokeObjectURL(url), 4000);
                                                toast.success('Lettre de voiture téléchargée', { id: 'lv-pdf' });
                                            } catch (e) {
                                                console.error('LV fetch failed', e);
                                                toast.error(`Erreur réseau : ${e?.message || 'inconnue'}`, { id: 'lv-pdf', duration: 10000 });
                                            }
                                        }}
                                        className="px-3 py-1.5 text-xs font-bold rounded-md border border-[#1B365D] text-[#1B365D] hover:bg-[#1B365D] hover:text-white transition-colors"
                                        data-testid="event-lv-btn"
                                        title="Telecharger la lettre de voiture"
                                    >
                                        LV
                                    </button>
                                    {(() => {
                                        const tc = TYPE_COLORS[detailData.type_prestation] || { bg: 'bg-gray-400', text: 'text-white' };
                                        return <Badge className={`${tc.bg} ${tc.text} text-xs`}>{detailData.type_prestation}</Badge>;
                                    })()}
                                </div>
                            </div>

                            <div className="p-6 space-y-5">
                                {/* Client info */}
                                <div className="border border-gray-200 rounded-xl p-4">
                                    <h3 className="text-xs font-bold text-gray-500 uppercase mb-3">Informations client</h3>
                                    <div className="space-y-2">
                                        <div className="flex items-center gap-2 text-sm">
                                            <User className="w-3.5 h-3.5 text-gray-400" />
                                            <span className="font-semibold text-gray-800">{detailData.client?.nom || '-'}</span>
                                        </div>
                                        <div className="flex items-center gap-2 text-sm">
                                            <Phone className="w-3.5 h-3.5 text-gray-400" />
                                            <span className="text-gray-600">{detailData.client?.telephone || '-'}</span>
                                        </div>
                                        <div className="flex items-center gap-2 text-sm">
                                            <Mail className="w-3.5 h-3.5 text-gray-400" />
                                            <span className="text-gray-600">{detailData.client?.email || '-'}</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Addresses */}
                                <div className="border border-gray-200 rounded-xl p-4">
                                    <h3 className="text-xs font-bold text-gray-500 uppercase mb-3">Demenagement</h3>
                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                        <div>
                                            <span className="text-emerald-500 text-xs font-bold flex items-center gap-1"><MapPin className="w-3 h-3" />DEPART</span>
                                            <p className="text-gray-700 mt-1">{detailData.client?.adresse_depart || '-'}</p>
                                            <div className="flex gap-4 mt-1.5">
                                                <span className="text-xs text-gray-400">Etage: <strong className="text-gray-600">{detailData.client?.etage_depart || '-'}</strong></span>
                                                <span className="text-xs text-gray-400">Ascenseur: <strong className="text-gray-600">{detailData.client?.ascenseur_depart || '-'}</strong></span>
                                            </div>
                                        </div>
                                        <div>
                                            <span className="text-red-400 text-xs font-bold flex items-center gap-1"><MapPin className="w-3 h-3" />ARRIVEE</span>
                                            <p className="text-gray-700 mt-1">{detailData.client?.adresse_arrivee || '-'}</p>
                                            <div className="flex gap-4 mt-1.5">
                                                <span className="text-xs text-gray-400">Etage: <strong className="text-gray-600">{detailData.client?.etage_arrivee || '-'}</strong></span>
                                                <span className="text-xs text-gray-400">Ascenseur: <strong className="text-gray-600">{detailData.client?.ascenseur_arrivee || '-'}</strong></span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Resources */}
                                {detailData.event_type === 'programmation' && (
                                    <div className="border border-gray-200 rounded-xl p-4">
                                        <h3 className="text-xs font-bold text-gray-500 uppercase mb-3">Ressources</h3>
                                        <div className="grid grid-cols-3 gap-3 text-sm">
                                            <div className="bg-gray-50 rounded-lg p-2 text-center">
                                                <span className="text-xs text-gray-400">Demenageurs</span>
                                                <p className="font-bold text-gray-800">{detailData.demenageurs || 0}</p>
                                            </div>
                                            <div className="bg-gray-50 rounded-lg p-2 text-center">
                                                <span className="text-xs text-gray-400">Camion 20m3</span>
                                                <p className="font-bold text-gray-800">{detailData.camion_20m3 || 0}</p>
                                            </div>
                                            <div className="bg-gray-50 rounded-lg p-2 text-center">
                                                <span className="text-xs text-gray-400">Camion 30m3</span>
                                                <p className="font-bold text-gray-800">{detailData.camion_30m3 || 0}</p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Fournitures for livraison */}
                                {detailData.livraison?.fournitures && (
                                    <div className="border border-gray-200 rounded-xl p-4">
                                        <h3 className="text-xs font-bold text-gray-500 uppercase mb-3">Fournitures</h3>
                                        <div className="grid grid-cols-2 gap-2 text-sm">
                                            {Object.entries(detailData.livraison.fournitures).map(([k, v]) => (
                                                <div key={k} className="flex justify-between bg-gray-50 rounded-lg px-3 py-2">
                                                    <span className="text-gray-600">{k.replace(/_/g, ' ')}</span>
                                                    <span className="font-bold">{v}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Payment / Acompte */}
                                <div className="border border-gray-200 rounded-xl p-4">
                                    <h3 className="text-xs font-bold text-gray-500 uppercase mb-3">Paiement</h3>
                                    {(() => {
                                        const total = Number(detailData.client?.total_ttc || 0);
                                        const recu = Number(detailData.acompte_recu || 0);
                                        const reste = Number(detailData.reste_a_payer || Math.max(total - recu, 0));
                                        const pct = total > 0 ? Math.round((recu / total) * 100) : 0;
                                        return (
                                            <div className="space-y-2 text-sm">
                                                <div className="flex items-center justify-between">
                                                    <span className="text-gray-600">Total TTC</span>
                                                    <span className="font-bold text-[#1B365D]" data-testid="event-total-ttc">
                                                        {total.toFixed(2)} EUR
                                                    </span>
                                                </div>
                                                <div className="flex items-center justify-between">
                                                    <span className="text-gray-600">Acompte recu{pct > 0 ? ` (${pct}%)` : ''}</span>
                                                    <span className="font-bold text-emerald-600" data-testid="event-acompte-recu">
                                                        {recu.toFixed(2)} EUR
                                                    </span>
                                                </div>
                                                <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                                                    <span className="text-gray-600">Reste a payer</span>
                                                    <span className={`font-bold ${reste > 0 ? 'text-orange-600' : 'text-gray-400'}`} data-testid="event-reste-a-payer">
                                                        {reste.toFixed(2)} EUR
                                                    </span>
                                                </div>
                                            </div>
                                        );
                                    })()}
                                    {detailData.livraison?.paiement && (
                                        <div className="mt-3 bg-emerald-50 rounded-lg p-2 text-xs">
                                            <span className="text-emerald-700 font-semibold">
                                                {PAIEMENT_OPTIONS.find(o => o.value === detailData.livraison.paiement.type)?.label || detailData.livraison.paiement.type}
                                            </span>
                                            {detailData.livraison.paiement.justificatif_url && (
                                                <a href={`${API_URL}${detailData.livraison.paiement.justificatif_url}`} target="_blank" rel="noreferrer"
                                                    className="ml-2 text-blue-500 underline">Voir justificatif</a>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Sous-traitance specific block */}
                                {detailData.event_type === 'sous_traitance' && (
                                    <STFactureSection
                                        eventId={detailData.id}
                                        detailData={detailData}
                                    />
                                )}

                                {/* Remarque */}
                                {(detailData.remarque || detailData.livraison?.remarque) && (
                                    <div className="border-l-2 border-l-amber-300 pl-4 py-2 bg-amber-50/50 rounded-r-lg">
                                        <span className="text-xs text-gray-400 font-medium">Remarque</span>
                                        <p className="text-sm text-gray-700">{detailData.remarque || detailData.livraison?.remarque}</p>
                                    </div>
                                )}

                                {/* Pièces jointes (autorisations stationnement, plans, etc.) */}
                                <EventPiecesJointes eventId={detailData.id} />
                            </div>

                            {/* Footer actions */}
                            <div className="flex justify-between items-center px-6 py-4 border-t border-gray-200 bg-gray-50">
                                <div className="flex items-center gap-2">
                                    {detailData.event_type === 'sous_traitance' ? (
                                        <Button
                                            variant="outline"
                                            className="text-sm gap-1.5"
                                            onClick={() => {
                                                setDetailOpen(false);
                                                openSousTraitanceForm(detailData.id);
                                            }}
                                            data-testid="event-edit-st-btn"
                                        >
                                            <Pencil className="w-4 h-4" /> Modifier
                                        </Button>
                                    ) : (
                                        <Button variant="outline" className="text-sm" onClick={() => navigate(`/clients/${detailData.client_id}`)} data-testid="event-goto-client">
                                            Voir fiche client
                                        </Button>
                                    )}
                                    {isAdmin && (
                                        <Button
                                            variant="outline"
                                            className="text-sm border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700 gap-1.5"
                                            onClick={() => setDeleteConfirmOpen(true)}
                                            data-testid="event-delete-btn"
                                        >
                                            <Trash2 className="w-4 h-4" /> Supprimer
                                        </Button>
                                    )}
                                </div>
                                {detailData.event_type === 'livraison' && detailData.statut_livraison !== 'livre' && (
                                    <Button className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2" onClick={handleLivraisonFaite} data-testid="livraison-faite-btn">
                                        <CheckCircle2 className="w-4 h-4" /> Livraison faite
                                    </Button>
                                )}
                                {detailData.statut_livraison === 'livre' && (
                                    <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200 text-xs">Livree</Badge>
                                )}
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Paiement Modal */}
            <Dialog open={paiementOpen} onOpenChange={setPaiementOpen}>
                <DialogContent className="max-w-md p-0" data-testid="paiement-modal">
                    <DialogTitle className="sr-only">Validation livraison</DialogTitle>
                    <div className="p-6 space-y-5">
                        <h2 className="text-lg font-bold text-[#1B365D]">Validation de la livraison</h2>
                        <p className="text-sm text-gray-500">Selectionnez le type de paiement :</p>

                        <div className="space-y-2">
                            {PAIEMENT_OPTIONS.map(opt => (
                                <button key={opt.value} onClick={() => setPaiementType(opt.value)}
                                    className={`w-full text-left px-4 py-3 rounded-xl border-2 text-sm font-medium transition-all ${
                                        paiementType === opt.value ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 hover:border-gray-300 text-gray-700'
                                    }`} data-testid={`paiement-opt-${opt.value}`}>
                                    <div className="flex items-center gap-2">
                                        <CreditCard className="w-4 h-4" />
                                        {opt.label}
                                    </div>
                                </button>
                            ))}
                        </div>

                        {/* File upload for cheque/caution */}
                        {PAIEMENT_OPTIONS.find(o => o.value === paiementType)?.needsFile && (
                            <div className="border-2 border-dashed border-amber-300 rounded-xl p-4 bg-amber-50/30">
                                <label className="flex flex-col items-center gap-2 cursor-pointer">
                                    <Upload className="w-6 h-6 text-amber-500" />
                                    <span className="text-sm text-amber-700 font-medium">
                                        {justificatifFile ? justificatifFile.name : 'Upload ou photo du justificatif'}
                                    </span>
                                    <input type="file" className="hidden" accept="image/*,.pdf"
                                        onChange={e => setJustificatifFile(e.target.files[0])}
                                        data-testid="justificatif-upload" />
                                </label>
                            </div>
                        )}

                        <div className="flex justify-end gap-2">
                            <Button variant="outline" onClick={() => setPaiementOpen(false)}>Annuler</Button>
                            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={submitPaiement} disabled={paiementSaving} data-testid="paiement-submit-btn">
                                {paiementSaving ? 'Enregistrement...' : 'Valider'}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Delete confirmation */}
            <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
                <AlertDialogContent data-testid="event-delete-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Supprimer l'evenement ?</AlertDialogTitle>
                        <AlertDialogDescription>
                            L'evenement sera retire du calendrier de demenagement. Les donnees client associees (devis, facture, acomptes) ne seront pas affectees. Cette action est irreversible depuis l'interface.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="event-delete-cancel">Annuler</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDeleteEvent}
                            disabled={deletingEvent}
                            className="bg-red-600 hover:bg-red-700 text-white"
                            data-testid="event-delete-confirm"
                        >
                            {deletingEvent ? 'Suppression...' : 'Supprimer'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Layout>
    );
}



/**
 * Vue Liste type Agenda iOS — empilage vertical sur 60 jours roulants.
 * En-tête mois centré inséré quand le mois change.
 * Chaque jour : grand chiffre + abréviation jour. Cartes événements colorées par type.
 */
function ListView({ days, loading, onEventClick, today }) {
    const DAY_ABBR_LOCAL = ['dim.', 'lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.'];
    const MOIS_LABELS = ['Janvier', 'Fevrier', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Aout', 'Septembre', 'Octobre', 'Novembre', 'Decembre'];

    const isToday = (d) => d.getDate() === today.getDate() && d.getMonth() === today.getMonth() && d.getFullYear() === today.getFullYear();
    const isPast = (d) => {
        const t = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        return d.getTime() < t.getTime();
    };

    if (loading) {
        return (
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm py-16 flex justify-center" data-testid="agenda-list-loading">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1B365D]" />
            </div>
        );
    }

    if (!days.length) {
        return (
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm py-16 text-center" data-testid="agenda-list-empty">
                <p className="text-sm text-gray-400">Aucune prestation planifiée dans les 60 prochains jours.</p>
            </div>
        );
    }

    let lastMonthKey = '';
    return (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden" data-testid="agenda-list-view">
            {days.map((d, idx) => {
                const monthKey = `${d.year}-${d.month}`;
                const showMonthHeader = monthKey !== lastMonthKey;
                lastMonthKey = monthKey;
                const td = isToday(d.date);
                const past = isPast(d.date);

                return (
                    <div key={d.dateKey}>
                        {showMonthHeader && (
                            <div
                                className="px-5 py-3 bg-gradient-to-b from-[#1B365D]/5 to-transparent border-b border-gray-100 text-center"
                                data-testid={`agenda-list-month-${monthKey}`}
                            >
                                <span className="text-base font-bold text-[#1B365D] tracking-tight">
                                    {MOIS_LABELS[d.month - 1]} <span className="text-[#C9A962]">{d.year}</span>
                                </span>
                            </div>
                        )}

                        <div
                            className={`flex gap-4 px-4 py-4 border-b border-gray-100 last:border-b-0 transition-colors ${
                                td ? 'bg-[#1B365D]/[0.04]' : past ? 'bg-gray-50/40' : 'bg-white hover:bg-gray-50/60'
                            }`}
                            data-testid={`agenda-list-day-${d.dateKey}`}
                        >
                            {/* Date column */}
                            <div className="flex flex-col items-center justify-start w-14 shrink-0 pt-1">
                                {td ? (
                                    <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#1B365D] text-white text-lg font-bold shadow-sm">
                                        {d.day}
                                    </span>
                                ) : (
                                    <span className={`text-2xl font-bold leading-none ${past ? 'text-gray-300' : 'text-gray-700'}`}>
                                        {d.day}
                                    </span>
                                )}
                                <span className={`mt-1 text-[11px] font-medium uppercase tracking-wide ${td ? 'text-[#1B365D]' : past ? 'text-gray-300' : 'text-gray-400'}`}>
                                    {DAY_ABBR_LOCAL[d.dow]}
                                </span>
                            </div>

                            {/* Events column */}
                            <div className="flex-1 min-w-0 space-y-2">
                                {/* Daily totals chip */}
                                {(() => {
                                    const tot = d.events.reduce((acc, ev) => ({
                                        eff: acc.eff + (ev.demenageurs || 0),
                                        c20: acc.c20 + (ev.camion_20m3 || 0),
                                        c30: acc.c30 + (ev.camion_30m3 || 0),
                                    }), { eff: 0, c20: 0, c30: 0 });
                                    if (tot.eff === 0 && tot.c20 === 0 && tot.c30 === 0) return null;
                                    return (
                                        <div
                                            className="inline-flex items-center gap-2 text-[11px] font-semibold text-[#1B365D] bg-[#1B365D]/5 border border-[#1B365D]/15 rounded-full px-3 py-1 mb-1"
                                            data-testid={`agenda-list-totals-${d.dateKey}`}
                                            title="Total ressources nécessaires sur la journée"
                                        >
                                            <span className="uppercase tracking-wide text-[10px] text-[#C9A962]">Total jour</span>
                                            {tot.eff > 0 && <span>{tot.eff} eff.</span>}
                                            {tot.c20 > 0 && <span>· {tot.c20} × 20m³</span>}
                                            {tot.c30 > 0 && <span>· {tot.c30} × 30m³</span>}
                                        </div>
                                    );
                                })()}
                                {d.events.map((ev) => {
                                    const tc = TYPE_COLORS[ev.type_prestation] || { bg: 'bg-gray-400', text: 'text-white', dot: 'bg-gray-400' };
                                    return (
                                        <button
                                            key={ev.id}
                                            onClick={() => onEventClick(ev.id)}
                                            className={`w-full text-left rounded-xl border transition-all overflow-hidden ${ev.cancelled ? 'bg-red-50 border-red-200 hover:border-red-300' : 'bg-white border-gray-200 hover:border-[#1B365D]/30 hover:shadow-sm'}`}
                                            data-testid={`agenda-list-event-${ev.id}`}
                                        >
                                            <div className="flex">
                                                {/* Color bar */}
                                                <div className={`w-1.5 shrink-0 ${ev.cancelled ? 'bg-red-400' : tc.bg}`} />
                                                <div className="flex-1 px-3 py-2.5 min-w-0">
                                                    <div className="flex items-center justify-between gap-2 mb-1">
                                                        <span className={`text-sm font-bold truncate ${ev.cancelled ? 'text-red-700 line-through' : 'text-[#1B365D]'}`}>
                                                            {ev.client_nom || 'Sans nom'}
                                                        </span>
                                                        {ev.heure && (
                                                            <span className={`text-xs font-semibold shrink-0 tabular-nums ${ev.cancelled ? 'text-red-500 line-through' : 'text-gray-500'}`}>
                                                                {ev.heure}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {ev.cancelled && (
                                                        <div className="flex items-center gap-1 mb-1.5">
                                                            <span className="inline-flex items-center gap-1 text-[10px] font-bold text-red-700 bg-red-100 border border-red-200 rounded px-1.5 py-0.5 uppercase tracking-wide">
                                                                Annulee
                                                            </span>
                                                            {ev.cancel_raison && (
                                                                <span className="text-[11px] text-red-600 italic truncate" title={ev.cancel_raison}>
                                                                    « {ev.cancel_raison} »
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}
                                                    <div className="flex items-center gap-1.5 mb-0.5">
                                                        <span className={`w-2 h-2 rounded-full ${tc.dot || tc.bg}`} />
                                                        <span className={`text-[11px] font-semibold uppercase tracking-wide ${ev.cancelled ? 'text-gray-400 line-through' : 'text-gray-600'}`}>
                                                            {ev.type_prestation}
                                                        </span>
                                                    </div>
                                                    {(ev.ville_arrivee || ev.ville_depart) && (
                                                        <div className="flex items-start gap-1 text-xs text-gray-500">
                                                            <MapPin className="w-3 h-3 mt-0.5 shrink-0" />
                                                            <span className="truncate">
                                                                {ev.ville_depart && ev.ville_arrivee
                                                                    ? `${ev.ville_depart} → ${ev.ville_arrivee}`
                                                                    : (ev.ville_arrivee || ev.ville_depart)}
                                                            </span>
                                                        </div>
                                                    )}
                                                    {(ev.demenageurs > 0 || ev.camion_20m3 > 0 || ev.camion_30m3 > 0) && (
                                                        <div className="flex items-center gap-2 mt-1 text-[11px] text-gray-500">
                                                            {ev.demenageurs > 0 && <span>{ev.demenageurs} eff.</span>}
                                                            {ev.camion_20m3 > 0 && <span>· {ev.camion_20m3} × 20m³</span>}
                                                            {ev.camion_30m3 > 0 && <span>· {ev.camion_30m3} × 30m³</span>}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
